// Constantes da Comunicação do módulo Solicitações (shape do atendimento).
//
// As 3 rotas de chat (listarConversas / listarMensagens / enviarMensagem), o
// painel de solicitações da conversa e a nota interna são REAIS (backend
// `aawz_requisicoes_comunicacao` via `con.*` no apiComunicacao). NÃO há mais
// modo mock. As fixtures que sobram abaixo (CONEXOES_FIXTURE,
// RESPOSTAS_RAPIDAS_FIXTURE e as variantes RESPONDER) alimentam apenas features
// WhatsApp-internas (conexões/respostas rápidas) que NÃO existem na visão
// externa do profissional — placeholders enquanto o modelo de dados não define
// se fazem sentido em Solicitações.
//
// Datas em "DD/MM/YYYY HH:mm" (formato do backend) para casar com
// parseBackendDate / formatMessageTime do message-formatter do atendimento.

// Modos da view adaptativa de Comunicação:
//  - GENERATOR: escritório (cliente) conversa com os times AAWZ — gera solicitações.
//  - RESPONDER: usuário interno AAWZ responde — escolhe o grupo do time
//    (conexão) e vê os escritórios/clientes que mandaram mensagem.
export const COMUNICACAO_MODE = Object.freeze({
  GENERATOR: 'generator',
  RESPONDER: 'responder',
})

// Escritório interno AAWZ que ATENDE as solicitações (backend: ESCRITORIO_INTERNO_REQUISICOES,
// configurável; em produção = 2976). RESPONDER = usuário interno DESSE escritório; qualquer
// outro escritório (cliente externo) cai em GENERATOR. Alinhado ao backend de requisições.
export const SOLICITACOES_INTERNAL_OFFICE_ID = 2976

// Escritório de TESTE do time (83, "White Label"). Administra o catálogo
// Backoffice junto com o interno: dá ao time acesso à TELA de gestão sem
// precisar da sessão do 2976.
//
// ATENÇÃO — não é sandbox. O catálogo é COMPARTILHADO e gravado sempre sob o
// escritório interno (o backend fixa `ID_ESCRITORIO_INTERNO` na escrita, senão
// a linha nasceria órfã). Serviço criado ou corretora desligada por aqui muda o
// que TODO escritório enxerga, inclusive em produção.
//
// Desde 27/08/2026 o alcance NÃO para mais no catálogo: o 83 foi equiparado ao
// interno e enxerga também a Operação AAWZ — a fila com solicitações de todos
// os parceiros.
//
// ESPELHO MANUAL do backend (`aawz_solicitacoes/constants.py`:
// ID_ESCRITORIO_TESTE_CATALOGO / ESCRITORIOS_ADMIN_CATALOGO). Abrir só um dos
// lados produz uma tela que renderiza e falha em CADA salvamento — o guard do
// backend rejeitaria cada PUT/POST com 403.
export const SOLICITACOES_TEST_OFFICE_ID = 83

// Quem age como BACKOFFICE (escritório executor): Operação AAWZ, fila de
// trabalho cross-escritório, modo RESPONDER da Comunicação e administração do
// catálogo.
//
// Decisão do usuário em 27/08/2026: o escritório de teste (83) passa a ser
// tratado como o interno, "nas mesmas condições e tudo mais". Antes a lista
// valia só para o catálogo — a Operação AAWZ seguia exclusiva do 2976.
//
// ATENÇÃO ao alcance: a Operação AAWZ lê solicitações de TODOS os escritórios
// parceiros. Isto é leitura cross-tenant real, sem recorte de ambiente.
//
// ESPELHO MANUAL do backend (`aawz_solicitacoes/constants.py`:
// ESCRITORIOS_BACKOFFICE). Abrir só um dos lados produz uma tela que renderiza
// e recebe 403 em cada chamada.
export const ESCRITORIOS_BACKOFFICE = [
  SOLICITACOES_INTERNAL_OFFICE_ID,
  SOLICITACOES_TEST_OFFICE_ID,
]

// Alias mantido porque o helper do catálogo importa este nome. Hoje é a MESMA
// lista — administrar o catálogo deixou de ser um subconjunto do backoffice
// quando o 83 foi equiparado ao interno.
export const ESCRITORIOS_ADMIN_CATALOGO = ESCRITORIOS_BACKOFFICE

// =================================================================== //
// STATUS DA SOLICITAÇÃO (visão cliente / GENERATOR)
// =================================================================== //
//
// Fonte do status, HOJE: status da CONVERSA do atendimento
// (aberta|aguardando|em_atendimento|escalada|fechada), aproximação enquanto o
// núcleo de Solicitações não expõe o status "de verdade" da solicitação.
//
// Para trocar a fonte no futuro basta mudar o getter `conversaStatus` que lê o
// campo (em conversation-list-item) — este MAP (status → rótulo + cor) e o
// SOLICITACOES_STATUS_FILTER_OPTIONS continuam valendo, desde que as chaves de
// status sejam as mesmas. Cores via TOKEN semântico do design-system (não hex):
// `BaseStatus` resolve nome de token para `var(--token)`.

export const SOLICITACOES_STATUS = Object.freeze({
  ABERTA: 'aberta',
  AGUARDANDO: 'aguardando',
  EM_ATENDIMENTO: 'em_atendimento',
  ESCALADA: 'escalada',
  FECHADA: 'fechada',
})

// status → { label PT-BR, color }. `color` é um nome de TOKEN do design-system
// que o BaseStatus resolve para `var(--token)`. Importante: só usar tokens que
// EXISTEM como CSS custom property em src/design/_vars.sass (`$theme-colors` /
// `$gray-colors` / `$colors`) — `--warning-new` NÃO é exposta como CSS var, só
// existe como SASS `$warning-new`, por isso "aguardando" usa `await` (amarelo).
//   aberta          → amarelo  (--await   #e0c200 — alinha com "Aberto" do pedido)
//   aguardando      → amarelo  (--await   #e0c200)
//   em_atendimento  → verde    (--success #56AF79)
//   escalada        → vermelho (--danger  #EB5757)
//   fechada         → cinza    (--secondary #8e8e8e — concluída)
export const SOLICITACOES_STATUS_MAP = Object.freeze({
  [SOLICITACOES_STATUS.ABERTA]: { label: 'Aberta', color: 'await' },
  [SOLICITACOES_STATUS.AGUARDANDO]: { label: 'Aguardando', color: 'await' },
  [SOLICITACOES_STATUS.EM_ATENDIMENTO]: {
    label: 'Atendimento',
    color: 'success',
  },
  [SOLICITACOES_STATUS.ESCALADA]: { label: 'Escalada', color: 'danger' },
  [SOLICITACOES_STATUS.FECHADA]: { label: 'Concluída', color: 'secondary' },
})

// Fallback para status desconhecido (defensivo — backend pode introduzir novos).
export const SOLICITACOES_STATUS_FALLBACK = Object.freeze({
  label: '',
  color: 'secondary',
})

// Valor do filtro segmentado que representa "sem filtro" (mostra todas). O
// BaseToggleButton tipa `value` como String|Number (não aceita null), então
// usamos este sentinel na UI e traduzimos para `null` ao filtrar/emitir.
export const SOLICITACOES_STATUS_FILTER_ALL = 'todas'

// Opções do filtro segmentado de status na visão cliente (GENERATOR):
// Todas (sem filtro) / Aguardando / Atendimento. `value` = status da
// conversa (ou o sentinel ALL para "sem filtro"). Usado pelo BaseToggleButton.
export const SOLICITACOES_STATUS_FILTER_OPTIONS = [
  { value: SOLICITACOES_STATUS_FILTER_ALL, text: 'Todas' },
  { value: SOLICITACOES_STATUS.AGUARDANDO, text: 'Aguardando' },
  { value: SOLICITACOES_STATUS.EM_ATENDIMENTO, text: 'Atendimento' },
]

// =================================================================== //
// COPY da visão CLIENTE (GENERATOR) — vocabulário de SOLICITAÇÃO.
// =================================================================== //
//
// Na visão cliente NÃO existe "Nova Conversa" (resíduo do atendimento): o
// cliente abre SOLICITAÇÕES e acompanha a conversa com os times. Os textos
// abaixo substituem a copy de atendimento ("Selecione ou inicie uma conversa",
// "Nenhuma conversa encontrada") por linguagem de solicitação. Centralizados
// aqui para não vazar magic string no template (regra de design-system) e para
// o RESPONDER continuar usando a copy original de atendimento.
export const SOLICITACOES_GENERATOR_COPY = Object.freeze({
  // Empty-state central (chat-window): nenhuma solicitação selecionada.
  emptyChatTitle: 'Nenhuma solicitação selecionada',
  emptyChatHint:
    'Selecione uma solicitação ao lado para ver a conversa com o time.',
  // Empty-state da lista (conversation-list): nenhuma solicitação aberta.
  emptyListTitle: 'Nenhuma solicitação aberta',
  emptyListHint:
    'Quando você abrir uma solicitação, a conversa com o time aparece aqui.',
})

// =================================================================== //
// STATUS DA SOLICITAÇÃO (núcleo real — painel de detalhes).
// =================================================================== //
//
// Status DA SOLICITAÇÃO (≠ status da CONVERSA acima). O núcleo de Solicitações
// expõe 3 estados: `aberto` | `em andamento` | `encerrado` (string com espaço,
// exatamente como o backend entrega em GET .../solicitacoes). Este MAP é usado
// SÓ no painel de detalhes (BaseStatus do card). A tag/filtro da LISTA de
// conversas continua usando o status da CONVERSA (SOLICITACOES_STATUS_MAP).
//
// `color` é nome de TOKEN do design-system resolvido por BaseStatus para
// `var(--token)`. Só tokens que EXISTEM como CSS custom property (geradas de
// `$theme-colors` em src/design/_vars.sass): `await` (#e0c200 amarelo),
// `success` (#56AF79 verde), `secondary` (#8e8e8e cinza). NÃO existe `--info`.
//   aberto       → amarelo (await)     — solicitação aberta, sem tratativa
//   em andamento → verde   (success)   — em tratativa pelo time
//   encerrado    → cinza   (secondary) — concluída
export const SOLICITACOES_PEDIDO_STATUS = Object.freeze({
  ABERTO: 'aberto',
  EM_ANDAMENTO: 'em andamento',
  ENCERRADO: 'encerrado',
})

export const SOLICITACOES_PEDIDO_STATUS_MAP = Object.freeze({
  [SOLICITACOES_PEDIDO_STATUS.ABERTO]: { label: 'Aberto', color: 'await' },
  [SOLICITACOES_PEDIDO_STATUS.EM_ANDAMENTO]: {
    label: 'Em andamento',
    color: 'success',
  },
  [SOLICITACOES_PEDIDO_STATUS.ENCERRADO]: {
    label: 'Concluída',
    color: 'secondary',
  },
})

// Fallback para status de solicitação desconhecido (defensivo — sem label → o
// painel esconde a tag em vez de exibir um chip vazio/errado).
export const SOLICITACOES_PEDIDO_STATUS_FALLBACK = Object.freeze({
  label: '',
  color: 'secondary',
})

// =================================================================== //
// SOLICITAÇÕES por conversa (painel lateral de detalhes — visão GENERATOR).
// =================================================================== //
//
// Cada conversa (time) carrega uma LISTA de solicitações exibida no painel
// direito da view, em formato carrossel (um card por vez). A estrutura é uma
// LISTA propositalmente — para trocar a apresentação (carrossel ↔ accordion)
// sem mexer no shape consumido pelos componentes. Carregada via
// apiComunicacao.listarSolicitacoesDaConversa (núcleo real). O shape que o
// painel consome: { id, codigo, titulo, area, status, responsavel_nome,
// data_inicio, nota }.
//
// Os labels do card (Área, Responsável, Data início) ficam centralizados em
// SOLICITACOES_DETALHE_CAMPOS para não vazar magic string no template. NÃO há
// Prioridade/Origem/"Aberta por" no núcleo — não exibimos esses campos.
//
// `responsavel_nome` pode ser null (sem responsável atribuído): a linha
// Responsável é OMITIDA pelo painel nesse caso (não mostra placeholder). Os
// demais campos caem no placeholder neutro quando vazios.

// Labels (label → chave do objeto solicitação) renderizados no painel, na ordem
// do design. Só os campos que o núcleo entrega. PT-BR com acentos reais.
// `hideWhenEmpty: true` → a linha some quando o valor é vazio/null (em vez de
// mostrar placeholder) — usado em Responsável (sem atribuição). `icon` é o nome
// do icon-svg exibido no IconWithBackground da linha (centralizado aqui para não
// hardcodar ícone no template).
export const SOLICITACOES_DETALHE_CAMPOS = [
  { label: 'Área', key: 'area', icon: 'category' },
  {
    label: 'Responsável',
    key: 'responsavel_nome',
    hideWhenEmpty: true,
    icon: 'user-2',
  },
  // "Criado por" = profissional do escritório que abriu a solicitação.
  // PENDENTE BACKEND: o núcleo ainda NÃO entrega este campo no GET de
  // solicitações. Campo assumido: `criado_por_nome` (mapeado em mapSolicitacao
  // com default ''). Enquanto não vier o dado, `hideWhenEmpty` mantém a linha
  // OCULTA — sem placeholder. Ao ligar o backend, basta enviar `criado_por_nome`.
  {
    label: 'Criado por',
    key: 'criado_por_nome',
    hideWhenEmpty: true,
    icon: 'user-2',
  },
  { label: 'Data início', key: 'data_inicio', isDate: true, icon: 'calendar' },
]

// Valor de campo ausente — placeholder neutro (em vez de string vazia/undefined).
export const SOLICITACOES_DETALHE_CAMPO_VAZIO = '—'

// =================================================================== //
// COPY da seção "Minhas anotações" (nota interna privada — visão GENERATOR).
// =================================================================== //
//
// Nota interna privada por solicitação ("só você vê") com auto-save (debounce).
// Centralizado para não vazar magic string no template (regra de design-system)
// e manter PT-BR com acentos reais. Os estados de save dirigem o rodapé de
// status (idle/salvando/salvo) — copy única por estado.
export const SOLICITACOES_NOTA_COPY = Object.freeze({
  title: 'Minhas anotações',
  privacyLabel: 'só você vê',
  placeholder: 'Escreva uma anotação...',
  statusSaving: 'Salvando...',
  statusSaved: 'Salvo automaticamente — visível só para você',
})

// Janela de debounce do auto-save da anotação (ms). ~700ms após parar de
// digitar — equilibra responsividade e não disparar um save por tecla.
export const SOLICITACOES_NOTA_DEBOUNCE_MS = 700

// =================================================================== //
// FIXTURES LEGADO (clone do atendimento) — conexões/conversas/mensagens
// por modo (GENERATOR/RESPONDER).
// =================================================================== //
//
// Mantidas como legado do clone do atendimento. Hoje `apiComunicacao` usa as
// rotas REAIS para conversas/mensagens e `listarConexoes` devolve lista vazia
// (a visão externa não tem conexões WhatsApp reais), então estas fixtures de
// conversa/conexão NÃO estão wired no runtime — só `RESPOSTAS_RAPIDAS_FIXTURE`
// (mais abaixo) ainda alimenta a feature de respostas rápidas. Preservadas para
// referência/reuso caso o RESPONDER ganhe backend.

// Conexões WhatsApp (status conectado para cobrir AC6 — indicadores de sync).
export const CONEXOES_FIXTURE = [
  {
    id: 'conexao-1',
    nome: 'Atendimento Solicitações',
    tipo: 'individual',
    status: 'conectado',
    profissionais: [],
  },
]

// Conversas individuais + grupos (cobre AC4 — toggle Todas/Conversas/Grupos).
export const CONVERSAS_FIXTURE = [
  {
    id: 'conversa-1',
    id_conexao: 'conexao-1',
    status: 'aberta',
    ultima_mensagem_em: '13/06/2026 16:21',
    ultima_mensagem_preview:
      'Perfeito, vamos analisar e retornamos ainda hoje.',
    ultima_mensagem_is_note: false,
    mensagens_nao_lidas: 2,
    assigned_to_nome: '',
    contato: {
      is_group: false,
      nome: 'Time Jurídico',
      nome_contato: 'Time Jurídico',
      nome_cliente: '',
      telefone: '5511990001111',
      foto_url: '',
    },
  },
  {
    id: 'conversa-2',
    id_conexao: 'conexao-1',
    status: 'aberta',
    ultima_mensagem_em: '11/06/2026 14:05',
    ultima_mensagem_preview: 'Quando o repasse do mês deve cair na conta?',
    ultima_mensagem_is_note: false,
    mensagens_nao_lidas: 0,
    assigned_to_nome: '',
    contato: {
      is_group: false,
      nome: 'Time Financeiro',
      nome_contato: 'Time Financeiro',
      nome_cliente: '',
      telefone: '5511990002222',
      foto_url: '',
    },
  },
  {
    id: 'conversa-3',
    id_conexao: 'conexao-1',
    status: 'aberta',
    ultima_mensagem_em: '12/06/2026 10:47',
    ultima_mensagem_preview: 'Já estamos reprocessando a cotização da conta.',
    ultima_mensagem_is_note: false,
    mensagens_nao_lidas: 1,
    assigned_to_nome: '',
    contato: {
      is_group: true,
      nome: 'Grupo Backoffice AAWZ',
      telefone: '5511990003333-group',
      foto_url: '',
    },
  },
  {
    id: 'conversa-4',
    id_conexao: 'conexao-1',
    status: 'aberta',
    ultima_mensagem_em: '13/06/2026 16:20',
    ultima_mensagem_preview: 'Há uma nova oportunidade de cross-sell.',
    ultima_mensagem_is_note: false,
    mensagens_nao_lidas: 0,
    assigned_to_nome: '',
    contato: {
      is_group: true,
      nome: 'Grupo Comercial Regional',
      telefone: '5511990004444-group',
      foto_url: '',
    },
  },
]

// Mensagens por conversa (cobre AC8 — entrada/saída + datas variadas).
export const MENSAGENS_FIXTURE_POR_CONVERSA = {
  'conversa-1': [
    {
      id: 'msg-1-1',
      id_conversa: 'conversa-1',
      direcao: 'entrada',
      tipo: 'texto',
      conteudo: 'Olá! Como podemos ajudar com a requisição de contrato?',
      status: 'entregue',
      is_note: false,
      reacoes: [],
      data_mensagem: '10/06/2026 09:12',
    },
    {
      id: 'msg-1-2',
      id_conversa: 'conversa-1',
      direcao: 'saida',
      tipo: 'texto',
      conteudo: 'Preciso revisar a cláusula de exclusividade do contrato.',
      status: 'lida',
      is_note: false,
      reacoes: [],
      data_mensagem: '10/06/2026 09:15',
    },
    {
      id: 'msg-1-3',
      id_conversa: 'conversa-1',
      direcao: 'entrada',
      tipo: 'texto',
      conteudo: 'Perfeito, vamos analisar e retornamos ainda hoje.',
      status: 'entregue',
      is_note: false,
      reacoes: [],
      data_mensagem: '13/06/2026 16:21',
    },
  ],
  'conversa-2': [
    {
      id: 'msg-2-1',
      id_conversa: 'conversa-2',
      direcao: 'entrada',
      tipo: 'texto',
      conteudo: 'Recebemos sua solicitação de fechamento de comissões.',
      status: 'entregue',
      is_note: false,
      reacoes: [],
      data_mensagem: '11/06/2026 14:02',
    },
    {
      id: 'msg-2-2',
      id_conversa: 'conversa-2',
      direcao: 'saida',
      tipo: 'texto',
      conteudo: 'Quando o repasse do mês deve cair na conta?',
      status: 'lida',
      is_note: false,
      reacoes: [],
      data_mensagem: '11/06/2026 14:05',
    },
  ],
  'conversa-3': [
    {
      id: 'msg-3-1',
      id_conversa: 'conversa-3',
      direcao: 'saida',
      tipo: 'texto',
      conteudo: 'A integração da conta XP está com dados divergentes.',
      status: 'lida',
      is_note: false,
      reacoes: [],
      data_mensagem: '12/06/2026 10:40',
    },
    {
      id: 'msg-3-2',
      id_conversa: 'conversa-3',
      direcao: 'entrada',
      tipo: 'texto',
      conteudo: 'Já estamos reprocessando a cotização da conta.',
      status: 'entregue',
      is_note: false,
      reacoes: [],
      data_mensagem: '12/06/2026 10:47',
    },
  ],
  'conversa-4': [
    {
      id: 'msg-4-1',
      id_conversa: 'conversa-4',
      direcao: 'entrada',
      tipo: 'texto',
      conteudo: 'Há uma nova oportunidade de cross-sell para o cliente.',
      status: 'entregue',
      is_note: false,
      reacoes: [],
      data_mensagem: '13/06/2026 16:20',
    },
  ],
}

// Respostas rápidas (cobre AC9 — painel ⚡ acionado por "/").
export const RESPOSTAS_RAPIDAS_FIXTURE = [
  {
    id: 'resp-1',
    titulo: 'Aguardando retorno',
    conteudo: 'Estamos analisando sua solicitação e retornamos em breve.',
  },
  {
    id: 'resp-2',
    titulo: 'Documentos recebidos',
    conteudo: 'Recebemos os documentos. Obrigado!',
  },
]

// =================================================================== //
// RESPONDER (interno AAWZ responde) — conexões = grupos de WhatsApp dos
// times; conversas = escritórios/clientes que mandaram mensagem.
// =================================================================== //

// Conexões = grupos de WhatsApp dos times AAWZ (o respondedor escolhe por qual
// time atender). Status conectado para cobrir os indicadores de sync.
export const CONEXOES_RESPONDER_FIXTURE = [
  {
    id: 'conexao-juridico',
    nome: 'Time Jurídico',
    tipo: 'individual',
    status: 'conectado',
    profissionais: [],
  },
  {
    id: 'conexao-financeiro',
    nome: 'Time Financeiro',
    tipo: 'individual',
    status: 'conectado',
    profissionais: [],
  },
  {
    id: 'conexao-backoffice',
    nome: 'Time de Backoffice',
    tipo: 'individual',
    status: 'conectado',
    profissionais: [],
  },
  {
    id: 'conexao-comercial',
    nome: 'Time Comercial',
    tipo: 'individual',
    status: 'conectado',
    profissionais: [],
  },
]

// Conversas = escritórios/clientes que mandaram mensagem para os times.
export const CONVERSAS_RESPONDER_FIXTURE = [
  {
    id: 'conversa-resp-1',
    id_conexao: 'conexao-juridico',
    status: 'aberta',
    ultima_mensagem_em: '13/06/2026 16:21',
    ultima_mensagem_preview: 'Precisamos revisar a cláusula de exclusividade.',
    ultima_mensagem_is_note: false,
    mensagens_nao_lidas: 3,
    assigned_to_nome: '',
    contato: {
      is_group: false,
      nome: 'Lumen Capital',
      nome_contato: 'Lumen Capital',
      nome_cliente: '',
      telefone: '5511980001111',
      foto_url: '',
    },
  },
  {
    id: 'conversa-resp-2',
    id_conexao: 'conexao-financeiro',
    status: 'aberta',
    ultima_mensagem_em: '12/06/2026 11:40',
    ultima_mensagem_preview: 'Quando o repasse do mês deve cair na conta?',
    ultima_mensagem_is_note: false,
    mensagens_nao_lidas: 0,
    assigned_to_nome: '',
    contato: {
      is_group: false,
      nome: 'Acme Investimentos',
      nome_contato: 'Acme Investimentos',
      nome_cliente: '',
      telefone: '5511980002222',
      foto_url: '',
    },
  },
  {
    id: 'conversa-resp-3',
    id_conexao: 'conexao-backoffice',
    status: 'aberta',
    ultima_mensagem_em: '12/06/2026 10:05',
    ultima_mensagem_preview: 'A cotização da conta XP está divergente.',
    ultima_mensagem_is_note: false,
    mensagens_nao_lidas: 1,
    assigned_to_nome: '',
    contato: {
      is_group: false,
      nome: 'Vértice Wealth',
      nome_contato: 'Vértice Wealth',
      nome_cliente: '',
      telefone: '5511980003333',
      foto_url: '',
    },
  },
  {
    id: 'conversa-resp-4',
    id_conexao: 'conexao-comercial',
    status: 'aberta',
    ultima_mensagem_em: '11/06/2026 09:18',
    ultima_mensagem_preview:
      'Temos uma oportunidade de cross-sell para avaliar.',
    ultima_mensagem_is_note: false,
    mensagens_nao_lidas: 0,
    assigned_to_nome: '',
    contato: {
      is_group: false,
      nome: 'Norte Asset',
      nome_contato: 'Norte Asset',
      nome_cliente: '',
      telefone: '5511980004444',
      foto_url: '',
    },
  },
]

// Mensagens por conversa do respondedor (entrada = cliente; saída = respondedor).
export const MENSAGENS_RESPONDER_FIXTURE_POR_CONVERSA = {
  'conversa-resp-1': [
    {
      id: 'msg-resp-1-1',
      id_conversa: 'conversa-resp-1',
      direcao: 'entrada',
      tipo: 'texto',
      conteudo: 'Precisamos revisar a cláusula de exclusividade do contrato.',
      status: 'entregue',
      is_note: false,
      reacoes: [],
      data_mensagem: '13/06/2026 16:10',
    },
    {
      id: 'msg-resp-1-2',
      id_conversa: 'conversa-resp-1',
      direcao: 'saida',
      tipo: 'texto',
      conteudo: 'Recebido. Vamos analisar e retornamos ainda hoje.',
      status: 'lida',
      is_note: false,
      reacoes: [],
      data_mensagem: '13/06/2026 16:21',
    },
  ],
  'conversa-resp-2': [
    {
      id: 'msg-resp-2-1',
      id_conversa: 'conversa-resp-2',
      direcao: 'entrada',
      tipo: 'texto',
      conteudo: 'Quando o repasse do mês deve cair na conta?',
      status: 'entregue',
      is_note: false,
      reacoes: [],
      data_mensagem: '12/06/2026 11:40',
    },
  ],
  'conversa-resp-3': [
    {
      id: 'msg-resp-3-1',
      id_conversa: 'conversa-resp-3',
      direcao: 'entrada',
      tipo: 'texto',
      conteudo: 'A cotização da conta XP está com dados divergentes.',
      status: 'entregue',
      is_note: false,
      reacoes: [],
      data_mensagem: '12/06/2026 09:58',
    },
    {
      id: 'msg-resp-3-2',
      id_conversa: 'conversa-resp-3',
      direcao: 'saida',
      tipo: 'texto',
      conteudo: 'Já estamos reprocessando a cotização da conta.',
      status: 'lida',
      is_note: false,
      reacoes: [],
      data_mensagem: '12/06/2026 10:05',
    },
  ],
  'conversa-resp-4': [
    {
      id: 'msg-resp-4-1',
      id_conversa: 'conversa-resp-4',
      direcao: 'entrada',
      tipo: 'texto',
      conteudo: 'Temos uma oportunidade de cross-sell para avaliar.',
      status: 'entregue',
      is_note: false,
      reacoes: [],
      data_mensagem: '11/06/2026 09:18',
    },
  ],
}

// Seletores por modo — centralizam o mapeamento modo → fixture set. A api só
// chama estes; quando o backend existir, esta camada de seleção some (o modo
// passa a ser implícito no servidor por usuário). Default = GENERATOR.
function isResponderMode(mode) {
  return mode === COMUNICACAO_MODE.RESPONDER
}

export function conexoesFixtureByMode(mode) {
  return isResponderMode(mode) ? CONEXOES_RESPONDER_FIXTURE : CONEXOES_FIXTURE
}

export function conversasFixtureByMode(mode) {
  return isResponderMode(mode) ? CONVERSAS_RESPONDER_FIXTURE : CONVERSAS_FIXTURE
}

export function mensagensFixtureByMode(mode, id_conversa) {
  const porConversa = isResponderMode(mode)
    ? MENSAGENS_RESPONDER_FIXTURE_POR_CONVERSA
    : MENSAGENS_FIXTURE_POR_CONVERSA
  return porConversa[id_conversa] || []
}
